import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import {
  useGetByTenantAssetListQuery,
  useGetByTenantDistributionsAssetswithalertsQuery
} from 'api/rtk-api'
import { set } from 'lodash'
import { useEffect, useState } from 'react'

import { TENANT_ID } from '../../../../modules/tradebuilder-v2/constants'
import { formatToServerDateFromStr } from '../../../../utilities/date'
import { formatUnits, parseToFixedPrecision } from '../../../../utilities/number'
import { LegacyDatePicker, MessageBox, NumberInput, OperationButton, Space } from '../../atoms'
import { CheckboxWithLabel } from '../../molecules'
import LinkIcon from '../../molecules/linkIcon'
import { StyledContainer } from './funds-reinvestment.styles'

const REINVESTMENT_UNIT_PRECISION = 0.000001

export interface ExDateReinvestment {
  mappedConfirmationId?: string
  numberOfUnits: number
  readOnly: boolean
  reinvestmentDate: string
}

export interface ExpectedReinvestmentData {
  centsPerUnit: number
  expectedUnits: number
  referenceHoldings: number
  totalDistribution: number
}

export type FundsReinvestmentProps = {
  exDate: string
  expectedReinvestmentData: ExpectedReinvestmentData
  finSwitchReinvestment: ExDateReinvestment
  finalReinvestment: ExDateReinvestment
  onSubmit?: (updatedReinvestment: ExDateReinvestment) => void
  readOnly: boolean
}

const FundsReinvestment = ({
  exDate,
  expectedReinvestmentData,
  finSwitchReinvestment,
  finalReinvestment,
  onSubmit,
  readOnly
}: FundsReinvestmentProps) => {
  const [isApproved, setIsApproved] = useState<boolean>(false)
  const [isUnitsAsExpected, setIsUnitsAsExpected] = useState(true)
  const [isValid, setIsValid] = useState<boolean>(false)
  const assetList = useGetByTenantAssetListQuery({ tenant: TENANT_ID })
  const assetsWithAlerts = useGetByTenantDistributionsAssetswithalertsQuery({ tenant: TENANT_ID })
  const [updatedReinvestment, setUpdatedReinvestment] =
    useState<ExDateReinvestment>(finalReinvestment)
  useEffect(() => {
    setUpdatedReinvestment(finalReinvestment)
  }, [finalReinvestment])
  useEffect(() => {
    if (isApproved) {
      const hasValidValue =
        updatedReinvestment.numberOfUnits > REINVESTMENT_UNIT_PRECISION ||
        updatedReinvestment.numberOfUnits === 0
      setIsValid(hasValidValue)
    } else {
      setIsValid(false)
    }
    if (expectedReinvestmentData !== undefined) {
      const diff = Math.abs(
        updatedReinvestment.numberOfUnits - expectedReinvestmentData.expectedUnits
      )
      if (diff > 0.01 * expectedReinvestmentData.expectedUnits) {
        setIsUnitsAsExpected(false)
      } else {
        setIsUnitsAsExpected(true)
      }
    }
  }, [isApproved, updatedReinvestment, expectedReinvestmentData])

  const onReinvestmentUpdate = (updatedValue: any, propertyName: string) => {
    const clonedUpdate = JSON.parse(JSON.stringify(updatedReinvestment)) as ExDateReinvestment
    set(clonedUpdate, propertyName, updatedValue)
    setUpdatedReinvestment(clonedUpdate)
  }
  const onUpdate = (updatedValueStr: string) => {
    if (updatedValueStr === '') {
      onReinvestmentUpdate('', 'numberOfUnits')
      return
    }
    const updatedValue = parseToFixedPrecision(updatedValueStr, 6)
    onReinvestmentUpdate(updatedValue, 'numberOfUnits')
  }
  const onDateUpdate = (updatedValue: Date) => {
    onReinvestmentUpdate(updatedValue.toUTCString(), 'reinvestmentDate')
  }

  const onSubmitHandler = () => {
    if (onSubmit) {
      onSubmit(updatedReinvestment)
      assetList.refetch()
      assetsWithAlerts.refetch()
    }
  }

  const onReset = () => {
    const clonedUpdate = JSON.parse(JSON.stringify(updatedReinvestment)) as ExDateReinvestment
    clonedUpdate.numberOfUnits = 0
    clonedUpdate.reinvestmentDate = formatToServerDateFromStr(exDate)
    setUpdatedReinvestment(clonedUpdate)
  }

  const renderLabelCell = (labelName: string) => <TableCell key="label">{labelName}</TableCell>

  const getFormattedCellValue = (cellValue: number, allowZero = false) => {
    if (!!cellValue || (allowZero && cellValue === 0)) return cellValue
    return '-'
  }

  const renderTextCell = (
    key: string,
    cellValue: number,
    cellValueReadonly: boolean,
    onValueChange: (updatedValue: string) => void,
    allowZero = false
  ) => {
    const readonly = readOnly || cellValueReadonly
    const formattedCellValue = getFormattedCellValue(cellValue, allowZero)
    return (
      <TableCell key={key}>
        {readonly && <div>{formattedCellValue}</div>}
        {!readonly && (
          <NumberInput
            max=""
            readOnly={readonly}
            type="number"
            value={formattedCellValue}
            onChange={event => onValueChange(event.target.value)}
          />
        )}
      </TableCell>
    )
  }

  const renderEditableDateCell = (
    key: string,
    cellValue: string,
    cellValueReadonly: boolean,
    onValueChange: (updatedValue: Date) => void
  ) => {
    const readonly = readOnly || cellValueReadonly
    const formattedCellValue = cellValue === undefined ? '-' : cellValue.toString()
    return (
      <TableCell key={key}>
        {readonly && <div>{formattedCellValue}</div>}
        {!readonly && (
          <LegacyDatePicker
            disabled={readonly}
            minDate={new Date(exDate)}
            testId="reinvestment-date"
            value={new Date(cellValue)}
            onChange={date => onValueChange(date)}
          />
        )}
      </TableCell>
    )
  }

  function formatReferenceData(value: number, dp: number) {
    if (value !== undefined) {
      return formatUnits(value, dp)
    }
    return 'Insufficient data to determine (no data available for this ex-date)'
  }

  return (
    <StyledContainer>
      <TableContainer classes={{ root: 'Q-Hub' }}>
        <Table aria-label="upcoming-reinvestment">
          <TableBody>
            <TableRow key="ref-holdings">
              {renderLabelCell('Reference holdings')}
              {renderLabelCell(formatReferenceData(expectedReinvestmentData?.referenceHoldings, 6))}
            </TableRow>
            <TableRow key="dist-cents-per-unit">
              {renderLabelCell('Distribution cents per unit')}
              {renderLabelCell(formatReferenceData(expectedReinvestmentData?.centsPerUnit, 6))}
            </TableRow>
            <TableRow key="total-distribution">
              {renderLabelCell('Total distribution amount')}
              {renderLabelCell(formatReferenceData(expectedReinvestmentData?.totalDistribution, 2))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <TableContainer classes={{ root: 'Q-Hub' }}>
        <Table aria-label="upcoming-reinvestment">
          <TableHead>
            <TableRow>
              <TableCell width="400px" />
              <TableCell width="200px">{readOnly ? '' : 'Finswitch value'}</TableCell>
              <TableCell width="200px">{readOnly ? '' : 'Final value*'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow key="units">
              {renderLabelCell(
                expectedReinvestmentData?.expectedUnits !== undefined
                  ? `Number of units (${formatUnits(
                      expectedReinvestmentData?.expectedUnits
                    )} expected)`
                  : 'Number of units'
              )}
              {renderTextCell(
                'finswitch-numberOfUnits',
                finSwitchReinvestment.numberOfUnits,
                finSwitchReinvestment.readOnly,
                onUpdate
              )}
              {renderTextCell(
                'final-numberOfUnits',
                updatedReinvestment.numberOfUnits,
                updatedReinvestment.readOnly,
                onUpdate,
                true
              )}
            </TableRow>

            <TableRow key="date">
              {renderLabelCell('Reinvestment date')}
              {renderEditableDateCell(
                'finswitch-reinvestmentDate',
                finSwitchReinvestment.reinvestmentDate,
                finSwitchReinvestment.readOnly,
                onDateUpdate
              )}
              {renderEditableDateCell(
                'final-reinvestmentDate',
                updatedReinvestment.reinvestmentDate,
                updatedReinvestment.readOnly,
                onDateUpdate
              )}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {!isUnitsAsExpected && (
        <Space mx={5} my={5}>
          <MessageBox state="warning" title="Unmatched units warning">
            The number of units entered do not match what was expected based on the distribution
            information received. Please check the number of units before proceeding.
          </MessageBox>
        </Space>
      )}
      {!readOnly && !finalReinvestment.readOnly && (
        <>
          <Space ml={5} my={5}>
            <CheckboxWithLabel
              checked={isApproved}
              data-testid="confirm-distribution-checkbox"
              onChange={val => setIsApproved(val)}
            >
              Please confirm the values to continue. Once set, they cannot be changed.
            </CheckboxWithLabel>
          </Space>
          <Space isflex flexDirection="row" mb={5} ml={5}>
            <OperationButton
              isDisabled={!isValid}
              operationType="progress"
              onClick={onSubmitHandler}
            >
              Submit
            </OperationButton>
            <Space mx={5} py={2}>
              <LinkIcon iconPosition="left" text="Ignore" onClick={onReset} />
            </Space>
          </Space>
        </>
      )}
    </StyledContainer>
  )
}

export default FundsReinvestment
